import { IsNotEmpty, IsString, IsOptional, IsDateString, MaxLength, IsArray, IsEnum, IsBoolean } from 'class-validator';
import {
    GeneralOverviewType,
    ActivityType,
    ServicesProvidedType,
    StorageType,
    DocumentControlType,
} from '../../../enums';

export class CreateClientInformationRecordRequestDto {
    /**
     * Document identifier for the client information record
     * @example 'QR-043-5'
     */
    @IsNotEmpty()
    @IsString()
    @MaxLength(50)
    documentNumber: string;

    /**
     * Name of the client organization
     * @example 'ACME Pharmaceuticals Canada Inc.'
     */
    @IsNotEmpty()
    @IsString()
    @MaxLength(255)
    clientName: string;

    /**
     * Physical address of the client organization
     * @example '123 Business Parkway, Suite 400, Toronto'
     */
    @IsOptional()
    @IsString()
    clientAddress?: string;

    /**
     * Website URL of the client organization
     * @example 'www.acmepharma.com/canada/'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    clientWebsite?: string;

    @IsOptional()
    @IsBoolean()
    mahHolder?: boolean;

    /**
     * Effective date of this client information record (ISO 8601 format)
     * @example '2024-01-15'
     */
    @IsNotEmpty()
    @IsDateString()
    asOfDate: string;

    /**
     * Type of service provided by Q&C Services to this client
     * @example 'QCO_SERVICES'
     */
    @IsOptional()
    @IsEnum(ServicesProvidedType)
    servicesProvidedByQc?: ServicesProvidedType;

    /**
     * General notes from overview section
     * @example 'Q&C SOPs & systems are used for all quality activities including disposition, complaint handling, and pharmacovigilance.'
     */
    @IsOptional()
    @IsString()
    generalOverviewNotes?: string;

    /**
     * Document control delivery preference
     * @example 'HARD_COPY'
     */
    @IsOptional()
    @IsEnum(DocumentControlType)
    documentControlToClient?: DocumentControlType;

    /**
     * Document Control Contact ID
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @IsOptional()
    @IsString()
    documentControlToClientContactId?: string;

    /**
     * Reference number for the Client Quality Manual
     * @example 'QM32'
     */
    @IsOptional()
    @IsString()
    @MaxLength(50)
    clientQualityManualReferenceNumber?: string;

    /**
     * List of contact IDs for the Client Quality Manual
     * @example ['uuid-1', 'uuid-2']
     */
    @IsOptional()
    @IsArray()
    @IsString({ each: true })
    clientQualityManualContactIds?: string[];

    /**
     * SOP training provided to client
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    sopTrainingProvided?: boolean;

    /**
     * SOP training delivered electronically
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    sopTrainingDeliveryElectronically?: boolean;

    /**
     * SOP training delivered onsite
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    sopTrainingDeliveryOnsite?: boolean;

    /**
     * Who sends/arranges the training
     * @example 'QCO'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    sopTrainingArrangedBy?: string;

    /**
     * GMP training provided to client
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    gmpTrainingProvided?: boolean;

    /**
     * GMP training delivered electronically
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    gmpTrainingDeliveryElectronically?: boolean;

    /**
     * GMP training delivered onsite
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    gmpTrainingDeliveryOnsite?: boolean;

    /**
     * Who sends/arranges the GMP training
     * @example 'QCO'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    gmpTrainingArrangedBy?: string;

    /**
     * Selected product storage types for this client
     * @example ['THIRD_PARTY_WAREHOUSE', 'OWN_WAREHOUSE']
     */
    @IsOptional()
    @IsArray()
    @IsEnum(StorageType, { each: true })
    productStorageConfigs?: StorageType[];

    /**
     * Selected general overview types for this client
     * @example ['OTC_DRUG', 'PRESCRIPTION_DRUG_LIST']
     */
    @IsOptional()
    @IsArray()
    @IsEnum(GeneralOverviewType, { each: true })
    generalOverview?: GeneralOverviewType[];

    /**
     * Selected licensable activities for this client
     * @example ['IMPORTER', 'DISTRIBUTOR']
     */
    @IsOptional()
    @IsArray()
    @IsEnum(ActivityType, { each: true })
    licensableActivities?: ActivityType[];

    /**
     * Version upgrade information for tracking changes
     * Only used when updating existing records
     * @example { "upgradeMajor": true, "targetVersion": { "major": 2, "minor": 0 } }
     */
    @IsOptional()
    versionUpgrade?: {
        upgradeMajor: boolean;
        targetVersion?: { major: number; minor: number };
    };
}
